// Copyright (c) 2022 by Move Closer

import { ComponentObjectPropsOptions } from '@movecloser/ui-core'
import { computed, onMounted, PropType, ref } from '@vue/composition-api'
import { ElementIdentifier } from '@movecloser/page-builder'

import { Container, IRelatedService, PickerCallback, PossibleRelatedPicker, Related } from '../../../../../contexts'
import { Emit } from '../../../../../dsl/vue-api'
import { logger } from '../../../../../support'
import { SnippetRelatedType } from '../../../../../contexts/content/related/snippet'

import { SnippetSelectorProps } from '../PageBuilder.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export const snippetSelectorProps: ComponentObjectPropsOptions<SnippetSelectorProps> = {
  container: {
    type: Object as PropType<Container>,
    required: true
  },
  pickRelated: {
    type: Function as PropType<PickerCallback>,
    required: true
  },
  relatedService: {
    type: Object as PropType<IRelatedService>,
    required: true
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export function useSnippetSelector (props: SnippetSelectorProps, emit: Emit) {
  const description = ref<string | null>(null)

  const snippetId = computed<ElementIdentifier | undefined>({
    get () {
      return props.container.source?.id
    },
    set (snippet) {
      emit('update:container', {
        ...props.container,
        source: { ...props.container.source, id: snippet }
      })
    }
  })

  const snippetLabel = computed<string>({
    get () {
      return props.container.source?.label ?? ''
    },
    set (label) {
      emit('update:container', {
        ...props.container,
        source: { ...props.container.source, label }
      })
    }
  })

  const hasSnippet = computed<boolean>(() => typeof snippetId.value !== 'undefined')

  const snippetRelated = computed<Related | undefined>(() => {
    if (typeof snippetId.value === 'undefined') {
      return undefined
    }

    return {
      type: SnippetRelatedType,
      value: snippetId.value
    }
  })

  function copyDescriptionAsLabel (): void {
    if (description.value === null) {
      return
    }

    snippetLabel.value = description.value
  }

  async function resolveDescription (related: Related): Promise<void> {
    try {
      description.value = (await props.relatedService.describe(related)).title
    } catch (e) {
      description.value = 'Cannot resolve snippet name'
      logger(e, 'warn')
    }
  }

  async function onSnippetSelection (related: Related): Promise<void> {
    snippetId.value = related.value

    await resolveDescription(related)
  }

  function selectSnippet (): void {
    props.pickRelated(PossibleRelatedPicker.Snippet, onSnippetSelection, snippetRelated.value)
  }

  onMounted(() => {
    if (props.container.source?.id) {
      resolveDescription({ type: SnippetRelatedType, value: props.container.source.id })
    }
  })

  return {
    copyDescriptionAsLabel,
    description,
    hasSnippet,
    selectSnippet,
    snippetId,
    snippetLabel
  }
}
